import React, { useState, useMemo, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import jsPDF from "jspdf";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { loadFactures } from "../config/firestore";

const C = {
  bg:     "#C4622D",
  header: "#2D5A3D",
  text:   "#1C1C1E",
  muted:  "#8A8A8E",
  terra:  "#C4622D",
  blue:   "#5B8DB8",
  gaz:    "#EF9F27",
  border: "#D3D1C7",
};

function fmt(n) {
  return Number(n || 0).toFixed(2).replace(".", ",") + " €";
}

function useStats(depenses, checks) {
  return useMemo(() => {
    const list   = depenses || [];
    const pleins = list.filter(d => d.type === "carburant");
    const sorted = [...pleins].sort((a, b) => Number(a.km) - Number(b.km));

    const kmCompteur = sorted.length > 0
      ? Number(sorted[sorted.length - 1].km)
      : 0;

    const kmTotal = sorted.length >= 2
      ? Number(sorted[sorted.length - 1].km) - Number(sorted[0].km)
      : 0;

    let litresCalc = 0, kmCalc = 0;
    for (let i = 1; i < sorted.length; i++) {
      const dKm = Number(sorted[i].km) - Number(sorted[i - 1].km);
      const L   = Number(sorted[i].litres);
      if (dKm > 0 && L > 0) { kmCalc += dKm; litresCalc += L; }
    }
    const conso100 = kmCalc > 0 ? (litresCalc / kmCalc) * 100 : 0;

    const totalAll       = list.reduce((s, d) => s + Number(d.montant || d.prix || 0), 0);
    const totalCarbu     = pleins.reduce((s, p) => s + Number(p.prix || 0), 0);
    const cat = (c) => list
      .filter(d => d.type === "general" && d.categorie === c)
      .reduce((s, d) => s + Number(d.montant || 0), 0);
    const totalFinancement   = cat("Financement");
    const totalAssurance     = cat("Assurance");
    const totalCtechnique    = cat("Contrôle technique");
    const totalGarage        = cat("Garage");
    const totalPeage         = cat("Péage");
    const totalLavage        = cat("Lavage");
    const totalEau           = cat("Eau");
    const totalGaz           = cat("Gaz");
    const totalCamping       = cat("Camping");
    const totalAire          = cat("Aire de repos");
    const totalParking       = cat("Parking");
    const totalHivernage     = cat("Hivernage");
    const totalContravention = cat("Contravention");
    const totalEntretien     = totalGarage + totalCtechnique;

    const monthsTrip  = new Set(pleins.map(p => p.date?.slice(0, 7)).filter(Boolean));
    const nbVoyages   = monthsTrip.size;
    const datesActive = new Set(list.map(d => d.date).filter(Boolean));
    const nbJours     = datesActive.size;

    const checkVals     = Object.values(checks || {});
    const verifications = checkVals.filter(v => v === "ok" || v === "warn" || v === "nok").length;
    const interventions = list.filter(d =>
      d.type === "general" &&
      ["Garage", "Contrôle technique"].includes(d.categorie)
    ).length;

    const sortedDates = list.map(d => d.date).filter(Boolean).sort();
    const lastCheckDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : null;

    return {
      kmCompteur, kmTotal, conso100,
      totalAll, totalCarbu, totalEntretien,
      totalFinancement, totalAssurance, totalCtechnique, totalGarage,
      totalPeage, totalLavage, totalEau, totalGaz,
      totalCamping, totalAire, totalParking, totalHivernage, totalContravention,
      nbVoyages, nbJours, verifications, interventions, lastCheckDate,
    };
  }, [depenses, checks]);
}

function Card({ children, style }) {
  return (
    <div style={{
      background: "white", borderRadius: 14, padding: "16px",
      marginBottom: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      border: `1px solid ${C.border}`,
      ...style,
    }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 12 }}>
      {children}
    </div>
  );
}

function DepBar({ label, amount, total, color }) {
  const pct = total > 0 ? Math.min(100, (amount / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{fmt(amount)}</span>
      </div>
      <div style={{ height: 7, background: "#EDEDEA", borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: color, borderRadius: 99,
          transition: "width 0.4s ease",
        }} />
      </div>
    </div>
  );
}

function useVoyageStats(voyage, depenses) {
  return useMemo(() => {
    const dep = voyage?.depart;
    const arr = voyage?.arrivee;
    if (!dep?.date || !arr?.date) return null;

    const depDate = new Date(dep.date);
    const arrDate = new Date(arr.date);
    const duree   = Math.round((arrDate - depDate) / 86400000);

    const kmParcourus = dep.km && arr.km ? Number(arr.km) - Number(dep.km) : null;

    const list = (depenses || []).filter(d => {
      if (!d.date) return false;
      const t = new Date(d.date);
      return t >= depDate && t <= arrDate;
    });

    const totalCarbu  = list.filter(d => d.type === "carburant")
      .reduce((s, d) => s + Number(d.prix || 0), 0);
    const cat = (c) => list.filter(d => d.type === "general" && d.categorie === c)
      .reduce((s, d) => s + Number(d.montant || 0), 0);
    const totalEau     = cat("Eau");
    const totalGaz     = cat("Gaz");
    const totalElec    = cat("Électricité");
    const totalCamping = cat("Camping");
    const totalAire    = cat("Aire de repos");
    const totalPeage   = cat("Péage");
    const totalAll     = totalCarbu + totalEau + totalGaz + totalElec + totalCamping + totalAire + totalPeage;

    const pleins = list.filter(d => d.type === "carburant");
    const sorted = [...pleins].sort((a, b) => Number(a.km) - Number(b.km));
    let litresCalc = 0, kmCalc = 0;
    for (let i = 1; i < sorted.length; i++) {
      const dKm = Number(sorted[i].km) - Number(sorted[i - 1].km);
      const L   = Number(sorted[i].litres);
      if (dKm > 0 && L > 0) { kmCalc += dKm; litresCalc += L; }
    }
    const conso100 = kmCalc > 0 ? (litresCalc / kmCalc) * 100 : 0;

    return {
      depDate: dep.date, arrDate: arr.date,
      duree, kmParcourus,
      totalAll, totalCarbu, totalEau, totalGaz, totalElec,
      totalCamping, totalAire, totalPeage,
      conso100,
    };
  }, [voyage, depenses]);
}

function certId(van) {
  const slug = (van?.nom || "VAN").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5).padEnd(5, "X");
  return `CVN-${slug}-${new Date().getFullYear()}`;
}

function compressToThumb(dataUrl, maxPx = 350, quality = 0.45) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale  = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

const TABS = [
  { id: "qr", label: "Passeport QR" },
];

export default function RapportScreen({ van, depenses, checks, userId, voyage, t = {} }) {
  const [tab,            setTab]            = useState("qr");
  const [garageFactures, setGarageFactures] = useState([]);
  const [facViewImg,     setFacViewImg]     = useState(null);
  const {
    kmCompteur, kmTotal, conso100,
    totalAll, totalCarbu,
    totalFinancement, totalAssurance, totalCtechnique, totalGarage,
    totalPeage, totalLavage, totalEau, totalGaz,
    totalCamping, totalAire, totalParking, totalHivernage, totalContravention,
    nbVoyages, nbJours, verifications, interventions, lastCheckDate,
  } = useStats(depenses, checks);

  const voyageStats = useVoyageStats(voyage, depenses);
  const id       = certId(van);
  const qrValue  = `https://checkvan.checkapp-studio.fr/certificat/${id}`;

  const interventionsList = useMemo(() => (depenses || [])
    .filter(d => d.type === "general" && ["Garage", "Contrôle technique"].includes(d.categorie))
    .map(d => ({
      date:        d.date        || null,
      km:          d.km          || null,
      description: d.description || d.categorie,
      cout:        Number(d.montant || 0),
    }))
    .sort((a, b) => (b.date || "").localeCompare(a.date || "")),
  [depenses]);

  useEffect(() => {
    if (tab !== "qr" || !id || !van) return;
    (async () => {
      try {
        // 1. Charger les factures Garage depuis Firestore (scan facture)
        const allFacs = await loadFactures(userId);
        const firestoreFacs = allFacs
          .filter(f => f.categorie === "Garage")
          .map(f => ({ factureId: String(f.factureId), image: f.image, date: f.date || "" }));

        // 2. Dépenses Garage avec photo saisie manuellement
        const depGarageWithPhoto = (depenses || [])
          .filter(d => d.type === "general" && d.categorie === "Garage" && d.image)
          .map(d => ({ factureId: "dep_" + String(d.id), image: d.image, date: d.date || "" }));

        // 3. Fusionner et dédupliquer, trier par date desc
        const allSources = [
          ...firestoreFacs,
          ...depGarageWithPhoto.filter(d => !firestoreFacs.some(f => f.factureId === d.factureId)),
        ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

        setGarageFactures(allSources);
        console.log("[QR sync] Garage:", firestoreFacs.length, "scan +", depGarageWithPhoto.length, "dépenses =", allSources.length);

        // 4. Comprimer chaque image en micro-thumbnail (350px)
        //    → stockées dans le document public certificats, accessible sans auth
        const rawThumbs = await Promise.all(
          allSources.map(async (f) => {
            const thumb = await compressToThumb(f.image, 350, 0.45);
            if (thumb) console.log("[QR sync] thumb:", Math.round(thumb.length / 1024), "Ko");
            return thumb ? { factureId: f.factureId, image: thumb, date: f.date } : null;
          })
        );
        const factures_garage = rawThumbs.filter(Boolean);
        const totalKo = Math.round(factures_garage.reduce((s, f) => s + f.image.length, 0) / 1024);
        console.log("[QR sync] total thumbnails:", factures_garage.length, "— taille totale:", totalKo, "Ko");

        // 3. Écrire dans certificats (document public, règles Firestore : allow read: if true)
        const ref  = doc(db, "certificats", id);
        const snap = await getDoc(ref);
        const now  = new Date().toISOString();
        await setDoc(ref, {
          vanNom:          van?.nom   || "",
          vanImmat:        van?.immat || "",
          certDisplayId:   id,
          updatedAt:       now,
          createdAt:       snap.exists() ? snap.data().createdAt : now,
          verifications,
          interventions,
          interventionsList,
          factures_garage,
        }, { merge: true });
        console.log("[QR sync] ✅ certificats mis à jour avec", factures_garage.length, "photo(s)");
      } catch (e) {
        console.error("[QR sync] ❌ Erreur:", e.message || e);
      }
    })();
  }, [tab, id, van, verifications, interventions, interventionsList, userId, depenses]);

  function handleDownloadPDF() {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;

    const GRN   = [45, 90, 61];
    const BG    = [248, 246, 241];
    const DARK  = [28, 28, 30];
    const MUTED = [138, 138, 142];
    const TERRA = [196, 98, 45];
    const BORD  = [211, 209, 199];
    const MINT  = [234, 243, 222];

    // ── HEADER VERT ────────────────────────────────────────────
    pdf.setFillColor(...GRN);
    pdf.rect(0, 0, W, 54, "F");

    // Van icon (dessiné en formes)
    const vx = 13, vy = 10;
    pdf.setFillColor(255, 255, 255);
    // Cargo box
    pdf.roundedRect(vx + 13, vy, 23, 16, 1.5, 1.5, "F");
    // Cab
    pdf.roundedRect(vx, vy + 4, 15, 12, 1.5, 1.5, "F");
    // Windshield (green)
    pdf.setFillColor(...GRN);
    pdf.rect(vx + 0.5, vy + 4.5, 9.5, 5.5, "F");
    // Cargo windows
    pdf.rect(vx + 15, vy + 2, 8, 5, "F");
    pdf.rect(vx + 25, vy + 2, 8, 5, "F");
    // Wheels
    pdf.setFillColor(22, 22, 22);
    pdf.circle(vx + 5,  vy + 18, 3.5, "F");
    pdf.circle(vx + 30, vy + 18, 3.5, "F");
    pdf.setFillColor(255, 255, 255);
    pdf.circle(vx + 5,  vy + 18, 1.5, "F");
    pdf.circle(vx + 30, vy + 18, 1.5, "F");
    // Headlight
    pdf.setFillColor(240, 200, 90);
    pdf.rect(vx + 0.5, vy + 8.5, 2, 2.5, "F");

    // Texte header
    pdf.setTextColor(248, 246, 241);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text("CHECKVAN", 58, 22);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(200, 230, 210);
    pdf.text("Certificat d'entretien véhicule", 58, 32);
    pdf.setFontSize(9);
    pdf.text(`Généré le ${new Date().toLocaleDateString("fr-FR")} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, 58, 41);
    // ID en haut-droite
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(id, W - 14, 41, { align: "right" });

    // ── VÉHICULE ────────────────────────────────────────────────
    let y = 64;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("VÉHICULE", 20, y);

    y += 7;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor(...DARK);
    pdf.text(van?.nom || "—", 20, y);

    y += 7;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(...MUTED);
    pdf.text(van?.immat || "—", 20, y);

    y += 6;
    pdf.setFontSize(10);
    pdf.setTextColor(...MUTED);
    pdf.text("Camping-car / Van", 20, y);

    // ── SÉPARATEUR ──
    y += 10;
    pdf.setDrawColor(...BORD);
    pdf.setLineWidth(0.25);
    pdf.line(20, y, W - 20, y);

    // ── INTERVENTIONS ────────────────────────────────────────────
    y += 11;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("INTERVENTIONS GARAGE", 20, y);

    y += 7;
    if (interventionsList.length === 0) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(...MUTED);
      pdf.text("Aucune intervention enregistrée.", 20, y);
      y += 10;
    } else {
      // En-tête tableau : Date | KM | Description | Montant
      pdf.setFillColor(...GRN);
      pdf.rect(20, y - 4.5, W - 40, 8, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(248, 246, 241);
      pdf.text("DATE",        24,     y);
      pdf.text("KM",          62,     y);
      pdf.text("DESCRIPTION", 90,     y);
      pdf.text("MONTANT",     W - 24, y, { align: "right" });
      y += 5;

      interventionsList.forEach((item, i) => {
        if (y > 255) {
          pdf.addPage();
          y = 20;
        }
        pdf.setFillColor(...(i % 2 === 0 ? BG : [255, 255, 255]));
        pdf.rect(20, y - 3.5, W - 40, 8, "F");
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(...DARK);
        pdf.text(item.date ? new Date(item.date).toLocaleDateString("fr-FR") : "—", 24, y + 1);
        pdf.text(item.km ? String(Math.round(item.km)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "—", 62, y + 1);
        pdf.text((item.description || "—").slice(0, 28), 90, y + 1);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...TERRA);
        pdf.text(item.cout > 0 ? fmt(item.cout) : "—", W - 24, y + 1, { align: "right" });
        y += 8;
      });
    }

    // ── CACHET CERTIFIÉ ──────────────────────────────────────────
    const stampY = Math.max(y + 12, 235);
    pdf.setFillColor(...MINT);
    pdf.setDrawColor(93, 202, 165);
    pdf.setLineWidth(0.7);
    pdf.roundedRect(20, stampY, W - 40, 32, 3, 3, "FD");

    // Cercle check
    pdf.setFillColor(...GRN);
    pdf.circle(36, stampY + 16, 8, "F");
    pdf.setDrawColor(255, 255, 255);
    pdf.setLineWidth(1.8);
    pdf.line(31.5, stampY + 16, 34.5, stampY + 19.5);
    pdf.line(34.5, stampY + 19.5, 41, stampY + 12);

    // Texte cachet
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(8, 80, 65);
    pdf.text("CERTIFIÉ CHECKVAN", 51, stampY + 13);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(8, 80, 65);
    pdf.text(id, 51, stampY + 21);
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`Horodatage : ${new Date().toLocaleString("fr-FR")}`, 51, stampY + 28);

    // ── FOOTER ───────────────────────────────────────────────────
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text(
      "Généré par CheckVan · Carnet de bord numérique · checkvan.checkapp-studio.fr",
      W / 2, 291, { align: "center" }
    );

    pdf.save(`checkvan-passeport-${(van?.immat || "van").toLowerCase().replace(/\s/g, "-")}.pdf`);
  }

  function handleDownloadVoyagePDF() {
    if (!voyageStats) return;
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;

    const GRN   = [45, 90, 61];
    const BG    = [248, 246, 241];
    const DARK  = [28, 28, 30];
    const MUTED = [138, 138, 142];
    const TERRA = [196, 98, 45];
    const BORD  = [211, 209, 199];
    const MINT  = [234, 243, 222];

    const fmtD = (s) => new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    const fmtKm = (n) => n != null ? String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " km" : "—";

    // ── HEADER ──────────────────────────────────────────────────
    pdf.setFillColor(...GRN);
    pdf.rect(0, 0, W, 46, "F");

    pdf.setTextColor(248, 246, 241);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(200, 230, 210);
    pdf.text("CHECKVAN", 20, 18);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor(248, 246, 241);
    pdf.text("Rapport de voyage", 20, 30);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(200, 230, 210);
    pdf.text(`Généré le ${new Date().toLocaleDateString("fr-FR")}`, 20, 40);

    // Van name right
    if (van?.nom) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(248, 246, 241);
      pdf.text(van.nom, W - 20, 26, { align: "right" });
      if (van?.immat) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(200, 230, 210);
        pdf.text(van.immat, W - 20, 35, { align: "right" });
      }
    }

    // ── PÉRIODE ─────────────────────────────────────────────────
    let y = 60;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("PÉRIODE DU VOYAGE", 20, y);

    y += 8;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(...DARK);
    pdf.text(`${fmtD(voyageStats.depDate)}  /  ${fmtD(voyageStats.arrDate)}`, 20, y);

    // ── SÉPARATEUR ──
    y += 10;
    pdf.setDrawColor(...BORD);
    pdf.setLineWidth(0.25);
    pdf.line(20, y, W - 20, y);

    // ── CHIFFRES CLÉS ────────────────────────────────────────────
    y += 12;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("CHIFFRES CLÉS", 20, y);

    const KW = (W - 40) / 3;
    y += 8;

    const drawKpi = (label, value, sub, x) => {
      pdf.setFillColor(...BG);
      pdf.roundedRect(x, y - 5, KW - 4, 22, 2, 2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(...GRN);
      pdf.text(value, x + (KW - 4) / 2, y + 8, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...MUTED);
      pdf.text(label, x + (KW - 4) / 2, y + 14, { align: "center" });
    };

    drawKpi("jours", String(voyageStats.duree >= 0 ? voyageStats.duree : "—"), "", 20);
    drawKpi("km parcourus", fmtKm(voyageStats.kmParcourus).replace(" km", ""), "", 20 + KW);

    // Troisième case : km départ / arrivée empilés
    const x3r = 20 + 2 * KW;
    pdf.setFillColor(...BG);
    pdf.roundedRect(x3r, y - 5, KW - 4, 22, 2, 2, "F");
    const depKmR = voyage?.depart?.km;
    const arrKmR = voyage?.arrivee?.km;
    const fmtKmBox = (n) => n ? String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " km" : "—";
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...GRN);
    pdf.text(fmtKmBox(depKmR), x3r + (KW - 4) / 2, y + 3, { align: "center" });
    pdf.setTextColor(...DARK);
    pdf.text(fmtKmBox(arrKmR), x3r + (KW - 4) / 2, y + 10, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("depart / arrivee", x3r + (KW - 4) / 2, y + 16, { align: "center" });

    // ── SÉPARATEUR ──
    y += 30;
    pdf.setDrawColor(...BORD);
    pdf.setLineWidth(0.25);
    pdf.line(20, y, W - 20, y);

    // ── DÉPENSES ─────────────────────────────────────────────────
    y += 12;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text("DÉPENSES DU VOYAGE", 20, y);

    y += 8;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(...DARK);
    pdf.text(fmt(voyageStats.totalAll), 20, y);

    y += 12;

    const rows = [
      { label: "Carburant",     amount: voyageStats.totalCarbu,   rgb: TERRA },
      { label: "Camping",       amount: voyageStats.totalCamping,  rgb: [124, 110, 168] },
      { label: "Aire de repos", amount: voyageStats.totalAire,     rgb: [91, 141, 184] },
      { label: "Péage",         amount: voyageStats.totalPeage,    rgb: [91, 141, 184] },
      { label: "Eau",           amount: voyageStats.totalEau,      rgb: [91, 141, 184] },
      { label: "Gaz",           amount: voyageStats.totalGaz,      rgb: [239, 159, 39]  },
      { label: "Électricité",  amount: voyageStats.totalElec,     rgb: [91, 141, 184]  },
    ].filter(r => r.amount > 0);

    // En-tête tableau
    pdf.setFillColor(...GRN);
    pdf.rect(20, y - 4.5, W - 40, 8, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(248, 246, 241);
    pdf.text("CATÉGORIE", 24, y);
    pdf.text("MONTANT", W - 24, y, { align: "right" });
    pdf.text("PART", W - 58, y, { align: "right" });
    y += 5;

    rows.forEach((row, i) => {
      pdf.setFillColor(...(i % 2 === 0 ? BG : [255, 255, 255]));
      pdf.rect(20, y - 3.5, W - 40, 9, "F");

      // Barre colorée à gauche
      pdf.setFillColor(...row.rgb);
      pdf.rect(20, y - 3.5, 3, 9, "F");

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...DARK);
      pdf.text(row.label, 27, y + 1.5);

      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...row.rgb);
      pdf.text(fmt(row.amount), W - 24, y + 1.5, { align: "right" });

      const pct = voyageStats.totalAll > 0
        ? Math.round((row.amount / voyageStats.totalAll) * 100)
        : 0;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...MUTED);
      pdf.text(`${pct} %`, W - 58, y + 1.5, { align: "right" });

      y += 9;
    });

    // ── CACHET ───────────────────────────────────────────────────
    y = Math.max(y + 16, 240);
    pdf.setFillColor(...MINT);
    pdf.setDrawColor(93, 202, 165);
    pdf.setLineWidth(0.7);
    pdf.roundedRect(20, y, W - 40, 26, 3, 3, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(8, 80, 65);
    pdf.text("Genere par CheckVan", W / 2, y + 11, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`checkvan.checkapp-studio.fr  -  ${new Date().toLocaleString("fr-FR")}`, W / 2, y + 19, { align: "center" });

    pdf.save(`checkvan-voyage-${(voyageStats.depDate || "").slice(0, 7)}.pdf`);
  }

  return (
    <div style={{ background: C.bg, minHeight: "100%", paddingBottom: 24 }}>

      {/* Header + tabs */}
      <div style={{ background: C.header, padding: "20px 20px 0" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#F8F6F1", textAlign: "center" }}>
          {t.rapport_titre || "Rapport"}
        </div>
      </div>

      <div style={{ padding: "16px 16px 0" }}>

        {/* ── Passeport QR ── */}
        {tab === "qr" && (
          <>
            {/* Identité */}
            <Card>
              <Label>{t.van || "VAN"}</Label>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text, lineHeight: 1.2 }}>
                {van?.nom || "—"}
              </div>
              <div style={{ fontSize: 14, color: C.muted, marginTop: 4, fontWeight: 600, letterSpacing: 1 }}>
                {van?.immat || "—"}
              </div>
            </Card>

            {/* QR code */}
            <Card style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px" }}>
              <div style={{
                background: C.bg, padding: 16, borderRadius: 14,
                border: `1.5px solid ${C.border}`,
              }}>
                <QRCodeSVG
                  value={qrValue}
                  size={160}
                  fgColor={C.header}
                  bgColor={C.bg}
                />
              </div>
              <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1 }}>
                {t.idCertificat || "ID CERTIFICAT"}
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginTop: 4, letterSpacing: 2 }}>
                {id}
              </div>
            </Card>

            {/* Stats publiques */}
            <Card>
              <Label>{t.statistiques || "STATISTIQUES"}</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{t.verificationsEffectuees || "Vérifications effectuées"}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{verifications}</span>
                </div>
                <div style={{ height: 1, background: C.border }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{t.interventionsEnregistrees || "Interventions enregistrées"}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{interventions}</span>
                </div>
                <div style={{ height: 1, background: C.border }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{t.derniereActivite || "Dernière activité"}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
                    {lastCheckDate
                      ? new Date(lastCheckDate).toLocaleDateString("fr-FR")
                      : "—"}
                  </span>
                </div>
              </div>
            </Card>

            {/* Factures Garage */}
            {garageFactures.length > 0 && (
              <Card>
                <Label>{t.facturesGarage || "FACTURES GARAGE"} ({garageFactures.length})</Label>

                {/* Modal plein écran */}
                {facViewImg && (
                  <div
                    onClick={() => setFacViewImg(null)}
                    style={{
                      position: "fixed", inset: 0, zIndex: 999,
                      background: "rgba(0,0,0,0.92)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      padding: 16,
                    }}
                  >
                    <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: 400, width: "100%" }}>
                      {facViewImg.date && (
                        <div style={{
                          background: C.header, borderRadius: "12px 12px 0 0",
                          padding: "10px 16px",
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: "#F8F6F1" }}>Garage</span>
                          <span style={{ fontSize: 12, color: "rgba(248,246,241,0.65)" }}>
                            {new Date(facViewImg.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                          </span>
                        </div>
                      )}
                      <img
                        src={facViewImg.image}
                        alt="Facture Garage"
                        style={{ width: "100%", display: "block", borderRadius: facViewImg.date ? "0 0 12px 12px" : 12 }}
                      />
                      <button
                        onClick={() => setFacViewImg(null)}
                        style={{
                          position: "absolute", top: -14, right: -14,
                          background: "white", border: "none", borderRadius: "50%",
                          width: 32, height: 32, fontSize: 14, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                        }}
                      >✕</button>
                    </div>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {garageFactures.map(f => (
                    <button
                      key={f.factureId}
                      onClick={() => setFacViewImg(f)}
                      style={{
                        padding: 0, border: "none", borderRadius: 10, overflow: "hidden",
                        cursor: "pointer", position: "relative", background: C.border, display: "block",
                      }}
                    >
                      <img src={f.image} alt="" style={{ width: "100%", height: 130, objectFit: "cover", display: "block" }} />
                      <div style={{
                        position: "absolute", bottom: 0, left: 0, right: 0,
                        background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                        padding: "18px 8px 7px",
                      }}>
                        {f.date && (
                          <div style={{ fontSize: 10, color: "white", fontWeight: 700 }}>
                            {new Date(f.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Bouton PDF */}
            <button
              onClick={handleDownloadPDF}
              style={{
                width: "100%", padding: "14px 0",
                background: C.header, color: "#F8F6F1",
                border: "none", borderRadius: 14,
                fontSize: 15, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 12px rgba(45,90,61,0.25)",
              }}
            >
              {t.telechargerPDF || "Télécharger PDF"}
            </button>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: (t.partagerTitre || "Passeport entretien") + " " + (van?.nom || ""),
                    text: (t.partagerTexte || "Voici le passeport d'entretien de mon camping-car") + " " + (van?.immat || van?.nom || ""),
                    url: qrValue,
                  });
                } else {
                  navigator.clipboard.writeText(qrValue).then(() => alert("Lien copié !"));
                }
              }}
              style={{
                width: "100%", padding: "14px 0", marginTop: 10,
                background: "#EAF3DE", color: "#2D5A3D",
                border: "2px solid #5DCAA5", borderRadius: 14,
                fontSize: 15, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {t.partagerPasseport || "📤 Partager le passeport"}
            </button>
          </>
        )}

      </div>
    </div>
  );
}
